import { Stack, Typography } from '@mui/material';
import { useEffect } from 'react';
import { useRouteError } from 'react-router-dom';

import { SmallTableEmptyState } from '@/components/display/SmallTableEmptyState/SmallTableEmptyState';
import { Button } from '@/components/form/baseInputs/Button';
import { useReportError } from '@/hooks/useReportError';

import type { AugmentedRoute } from './constants';

export function LazyRouteErrorState() {
  // Turbopack retries network failures once. After that, a rejected import can
  // stay cached, so recovery needs a document reload, not another import call.
  return (
    <SmallTableEmptyState
      fill="viewport"
      heading="Unable to load content"
      description="A network error prevented us from fully loading some content. Please check your connection, then retry."
      action={
        <Stack spacing={2} alignItems="center">
          <Button
            variant="primary"
            size="sm"
            onClick={() => window.location.reload()}
          >
            Retry
          </Button>
          <Typography variant="body-xs">
            Retrying reloads the page. Unsaved changes may be lost.
          </Typography>
        </Stack>
      }
    />
  );
}

function LazyRouteErrorBoundary() {
  const error = useRouteError();
  const { reportError } = useReportError();
  const isChunkLoadError =
    error instanceof Error && error.name === 'ChunkLoadError';

  useEffect(() => {
    if (isChunkLoadError) {
      reportError('Lazy route could not load', error);
    }
  }, [error, isChunkLoadError, reportError]);

  // Rethrowing reaches the nearest ancestor boundary, which can be a component
  // boundary around an Outlet rather than the root route boundary.
  if (!isChunkLoadError) throw error;

  return <LazyRouteErrorState />;
}

export function withLazyRouteErrorBoundaries(
  routes: AugmentedRoute[]
): AugmentedRoute[] {
  return routes.map((route) => {
    const loadRoute = route.lazy;
    return {
      ...route,
      ...(typeof loadRoute === 'function'
        ? {
            lazy: async (...args: Parameters<typeof loadRoute>) => {
              try {
                return await loadRoute(...args);
              } catch (error) {
                // React Router 7.18 can hide a boundary while a rejected lazy
                // route remains unresolved. Settle the route, then throw during
                // render so its static boundary receives the original error.
                return {
                  Component: () => {
                    throw error;
                  },
                };
              }
            },
          }
        : {}),
      // This boundary must be available before the route's lazy import starts.
      ...(!route.errorElement && !route.ErrorBoundary && route.lazy
        ? { errorElement: <LazyRouteErrorBoundary /> }
        : {}),
      ...(route.children
        ? { children: withLazyRouteErrorBoundaries(route.children) }
        : {}),
    };
  });
}
